import { getFormSetting, updateFormSetting } from '@/lib/api/formSettingApi'
import { RootState } from '@/redux/store'
import { FormSettingAttributes, GetFormSettingQuery } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { createFormThunk } from './formSlice'
import { QueryState } from './queryStateType'

export type ShallowFormSetting = Partial<NonNullable<GetFormSettingQuery['form']>['setting']> &
  QueryState & {
    formToken: string
  }

const formSettingAdapter = createEntityAdapter<ShallowFormSetting, string>({
  selectId: model => model.formToken,
})

export const formSettingSlice = createSlice({
  name: 'formSetting',
  initialState: formSettingAdapter.getInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFormSettingThunk.pending, (state, action) => {
        const formToken = action.meta.arg.formToken
        formSettingAdapter.upsertOne(state, {
          formToken,
          isLoading: true,
        } as ShallowFormSetting)
      })
      .addCase(fetchFormSettingThunk.fulfilled, (state, action) => {
        const { formToken, ...setting } = action.payload
        formSettingAdapter.upsertOne(state, {
          formToken,
          ...setting,
          isLoading: false,
          loaded: true,
          error: null,
        })
      })
      .addCase(fetchFormSettingThunk.rejected, (state, action) => {
        const formToken = action.meta.arg.formToken
        formSettingAdapter.updateOne(state, {
          id: formToken,
          changes: {
            isLoading: false,
            error: action.error.message,
          },
        })
      })
      .addCase(updateFormSettingThunk.fulfilled, (state, action) => {
        const { formToken, ...setting } = action.payload
        formSettingAdapter.updateOne(state, {
          id: formToken,
          changes: setting,
        })
      })
      // -------- subscribe formSlice thunks --------
      .addCase(createFormThunk.fulfilled, (state, action) => {
        const { token, ...setting } = action.payload
        formSettingAdapter.addOne(state, {
          formToken: token,
          ...setting,
          isLoading: false,
          loaded: true,
          error: null,
        })
      })
  },
})

type FetchFormSettingArg = {
  formToken: string
}

export const fetchFormSettingThunk = createAsyncThunk(
  'formSetting/fetchFormSetting',
  async (arg: FetchFormSettingArg, thunkAPI) => {
    const { formToken } = arg
    const {
      data: { form },
    } = await getFormSetting({ formToken })
    return form?.setting
      ? {
          ...form.setting,
          formToken,
        }
      : thunkAPI.rejectWithValue('failed to fetch formSetting')
  }
)

type UpdateFormSettingArg = {
  formToken: string
  setting: FormSettingAttributes
}

export const updateFormSettingThunk = createAsyncThunk(
  'formSetting/updateFormSetting',
  async (arg: UpdateFormSettingArg, thunkAPI) => {
    const { formToken, setting } = arg
    const { data } = await updateFormSetting({ formToken, attributes: setting })
    const updatedSetting = data?.updateFormSetting?.setting
    const errors = data?.updateFormSetting?.errors

    if (errors) {
      return thunkAPI.rejectWithValue(errors)
    }

    return { ...updatedSetting, formToken }
  }
)

const formSettingSelectors = formSettingAdapter.getSelectors()

export const selectFormSettingById = createSelector(
  (state: RootState) => state.formSettings,
  (_state: RootState, id?: string | null) => id,
  (state, id) => (id ? formSettingSelectors.selectById(state, id) : undefined)
)

export default formSettingSlice.reducer
